import React from "react";
import { FormControl, FormLabel, Switch, Stack, Option, Box, Checkbox, Sheet } from "@mui/joy";
import CommonModal from "../../../common/CommonModal";
import DarkInput from "../../../common/DarkInput";
import DarkSelect from "../../../common/DarkSelect";
import DarkTypography from "../../../common/DarkTypography";
import type { Prestation, PrestationRequest } from "../../../../api/billing/Billing.typed";
import { colorTokens } from "../../../../styles/globalStyles";
import { useTraining } from "../../../../contexts/api";
import {
  TAX_RATES,
  DEFAULT_TAX_RATE,
  formatNum,
  htToTtc,
  ttcToHt,
} from "../../shared/billingUtils";
import GroupAutocomplete from "../../shared/GroupAutocomplete";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (req: PrestationRequest) => Promise<unknown>;
  initial?: Prestation | null;
};

export default function PrestationFormModal({ open, onClose, onSubmit, initial }: Props) {
  const { qualifications, refreshTrainingTemplates } = useTraining();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [groupName, setGroupName] = React.useState("");
  const [unitPrice, setUnitPrice] = React.useState(0);
  const [ttcInput, setTtcInput] = React.useState("");
  const [currency] = React.useState("EUR");
  const [taxRate, setTaxRate] = React.useState(DEFAULT_TAX_RATE);
  const unit = "unit";
  const [trainingTemplateIds, setTrainingTemplateIds] = React.useState<number[]>([]);
  const [isActive, setIsActive] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open && !qualifications) {
      refreshTrainingTemplates();
    }
  }, [open, qualifications, refreshTrainingTemplates]);

  React.useEffect(() => {
    if (open && initial) {
      setName(initial.Name);
      setDescription(initial.Description);
      setGroupName(initial.GroupName ?? "");
      setUnitPrice(initial.UnitPrice);
      setTaxRate(initial.TaxRate);
      setTtcInput(formatNum(htToTtc(initial.UnitPrice, initial.TaxRate)));
      setTrainingTemplateIds(initial.TrainingTemplateIDs ?? []);
      setIsActive(initial.IsActive);
    } else if (open) {
      setName("");
      setDescription("");
      setGroupName("");
      setUnitPrice(0);
      setTtcInput("0");
      setTaxRate(DEFAULT_TAX_RATE);
      setTrainingTemplateIds([]);
      setIsActive(true);
    }
  }, [open, initial]);

  const handleConfirm = async () => {
    setSubmitting(true);
    await onSubmit({
      name,
      description,
      group_name: groupName,
      unit_price: unitPrice,
      currency,
      tax_rate: taxRate,
      unit,
      training_template_ids: trainingTemplateIds,
      is_active: isActive,
    });
    setSubmitting(false);
    onClose();
  };

  const isValid = name.trim().length > 0;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={initial ? "Modifier la prestation" : "Nouvelle prestation"}
      size="xl"
      fitContentHeight
      onConfirm={handleConfirm}
      confirmLabel={initial ? "Enregistrer" : "Créer"}
      submitDisabled={!isValid}
      loading={submitting}
    >
      <Stack spacing={2}>
        <FormControl required>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Nom</FormLabel>
          <DarkInput value={name} onChange={(e) => setName(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Description</FormLabel>
          <DarkInput value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormControl>
        <GroupAutocomplete value={groupName} onChange={setGroupName} entity="prestations" />
        <FormControl sx={{ width: 280 }}>
          <FormLabel sx={{ color: colorTokens.text.muted }}>TVA</FormLabel>
          <DarkSelect
            value={taxRate}
            onChange={(_, v) => {
              if (v === null) return;
              const newRate = v as number;
              setTaxRate(newRate);
              setTtcInput(formatNum(htToTtc(unitPrice, newRate)));
            }}
          >
            {TAX_RATES.map((t) => (
              <Option key={t.value} value={t.value}>
                {t.label}
              </Option>
            ))}
          </DarkSelect>
        </FormControl>
        <Sheet
          sx={{
            backgroundColor: colorTokens.bg.surfaceMuted,
            border: `1px solid ${colorTokens.border.default}`,
            borderRadius: "8px",
            p: 2,
          }}
        >
          <DarkTypography level="title-sm" sx={{ mb: 1.5 }}>
            Calculette de prix
          </DarkTypography>
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <FormControl required sx={{ flex: 1 }}>
              <FormLabel sx={{ color: colorTokens.text.muted }}>Prix unitaire HT</FormLabel>
              <DarkInput
                type="number"
                slotProps={{ input: { step: "0.01", min: "0" } }}
                value={unitPrice}
                onChange={(e) => {
                  const ht = parseFloat(e.target.value) || 0;
                  setUnitPrice(ht);
                  setTtcInput(formatNum(htToTtc(ht, taxRate)));
                }}
              />
            </FormControl>
            <DarkTypography level="body-lg" sx={{ pb: 0.5 }}>
              ⇄
            </DarkTypography>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel sx={{ color: colorTokens.text.muted }}>Prix unitaire TTC</FormLabel>
              <DarkInput
                type="number"
                slotProps={{ input: { step: "0.01", min: "0" } }}
                value={ttcInput}
                onChange={(e) => {
                  const raw = e.target.value;
                  setTtcInput(raw);
                  const ttc = parseFloat(raw) || 0;
                  setUnitPrice(ttcToHt(ttc, taxRate));
                }}
              />
            </FormControl>
          </Stack>
          <DarkTypography level="body-xs" colorVariant="muted" sx={{ mt: 1 }}>
            Seul le prix HT est enregistré. Le TTC est calculé avec la TVA sélectionnée.
          </DarkTypography>
        </Sheet>
        {qualifications && qualifications.length > 0 && (
          <FormControl>
            <FormLabel sx={{ color: colorTokens.text.muted }}>Formations associées</FormLabel>
            <Sheet
              sx={{
                backgroundColor: colorTokens.bg.surfaceMuted,
                border: `1px solid ${colorTokens.border.default}`,
                borderRadius: "8px",
                p: 1.5,
                maxHeight: 180,
                overflowY: "auto",
              }}
            >
              <Stack spacing={0.5}>
                {qualifications.map((q) => (
                  <Checkbox
                    key={q.ID}
                    label={`${q.Agency} – ${q.Qualification}`}
                    size="sm"
                    checked={trainingTemplateIds.includes(q.ID)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setTrainingTemplateIds((prev) => [...prev, q.ID]);
                      } else {
                        setTrainingTemplateIds((prev) => prev.filter((id) => id !== q.ID));
                      }
                    }}
                    sx={{ color: colorTokens.text.primary }}
                  />
                ))}
              </Stack>
            </Sheet>
          </FormControl>
        )}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <DarkTypography level="body-sm">Actif</DarkTypography>
          <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        </Box>
      </Stack>
    </CommonModal>
  );
}
